import React, { useState, useMemo } from "react";
import { buildItemsEvalNatParCategorie } from "../../../utils/evalnatfunction";

/**
 * Modal affichant le bilan détaillé d'un élève pour l'évaluation nationale
 *
 * @param {Object} props
 * @param {boolean} props.show - Afficher/masquer le modal
 * @param {Function} props.onClose - Fonction de fermeture
 * @param {Object} props.eleve - Données de l'élève (nom_eleve, id_eleve, reponses, etc.)
 * @param {Object} props.EvalNat - Données de l'évaluation nationale (questions, matiere, etc.)
 */
function ResultatEvalNatByEleve({ show, onClose, eleve, EvalNat, initialTab }) {

    const [activeTab, setActiveTab] = useState(null);

    // Extraire prénom et nom depuis nom_eleve (format "NOM Prénom")
    const { prenom, nom, initial } = useMemo(() => {
        if (!eleve?.nom_eleve) return { prenom: '', nom: '', initial: '?' };
        const parts = eleve.nom_eleve.split(' ');
        const nomPart = parts[0] || '';
        const prenomPart = parts.slice(1).join(' ') || '';
        return {
            nom: nomPart,
            prenom: prenomPart,
            initial: nomPart.charAt(0).toUpperCase()
        };
    }, [eleve]);

    // Générer une couleur basée sur le nom
    const avatarColor = useMemo(() => {
        if (!eleve?.nom_eleve) return '#0EA5E9';
        const colors = ['#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#6366F1'];
        let hash = 0;
        for (let i = 0; i < eleve.nom_eleve.length; i++) {
            hash = eleve.nom_eleve.charCodeAt(i) + ((hash << 5) - hash);
        }
        return colors[Math.abs(hash) % colors.length];
    }, [eleve]);

    // Toutes les catégories + items (logique factorisée dans le helper EvalNat)
    const toutesCategories = useMemo(
        () => buildItemsEvalNatParCategorie(eleve, EvalNat),
        [eleve, EvalNat],
    );

    // Onglets disponibles
    const categories = useMemo(
        () => toutesCategories.map((c) => ({ key: c.key, label: c.label })),
        [toutesCategories],
    );

    // Initialiser l'onglet actif (catégorie demandée si fournie, sinon la 1re)
    React.useEffect(() => {
        if (categories.length > 0 && !activeTab) {
            setActiveTab(
                initialTab && categories.some((c) => c.key === initialTab)
                    ? initialTab
                    : categories[0].key,
            );
        }
    }, [categories, activeTab, initialTab]);

    // Réponses de l'onglet actif, dans la forme attendue par le rendu ci-dessous.
    const eleveReponses = useMemo(() => {
        if (!activeTab) return [];
        const cat = toutesCategories.find((c) => c.key === activeTab);
        if (!cat) return [];
        return cat.items.map((it) => ({
            questionId: it.questionId,
            questionLabel: it.questionLabel,
            reponse: it.reponse,
            bonneReponse: it.bonneReponse,
            isCorrect: it.etat === 'reussi',
            score: it.score,
        }));
    }, [toutesCategories, activeTab]);

    // Calculer les stats globales (toutes catégories)
    const stats = useMemo(() => {
        if (!eleve?.reponses) return { reussites: 0, echecs: 0, total: 0, score: 0 };

        let reussites = 0;
        let echecs = 0;

        // Parcourir toutes les catégories de réponses
        Object.entries(eleve.reponses).forEach(([key, value]) => {
            // Ignorer les clés qui ne sont pas des catégories de questions
            if (typeof value !== 'object' || typeof value === 'string') return;

            Object.values(value).forEach(reponse => {
                if (typeof reponse === 'object' && 'score' in reponse) {
                    if (reponse.score === 1) {
                        reussites++;
                    } else {
                        echecs++;
                    }
                }
            });
        });

        const total = reussites + echecs;
        const score = total > 0 ? Math.round((reussites / total) * 100) : 0;

        return { reussites, echecs, total, score };
    }, [eleve]);

    // Fonction pour obtenir la couleur du score
    const getScoreColor = (score) => {
        if (score >= 70) return '#10B981';
        if (score >= 50) return '#F59E0B';
        return '#EF4444';
    };

    if (!show || !eleve) return null;

    return (
        <>
            {/* Overlay */}
            <div
                onClick={onClose}
                className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center animate-fadeIn"
            >
                {/* Modal Content */}
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-white rounded-2xl w-[95%] sm:w-[90%] max-w-[700px] xl:max-w-[600px] 2xl:max-w-[650px] 3xl:max-w-[700px] max-h-[90vh] xl:max-h-[85vh] overflow-hidden shadow-2xl animate-slideUp flex flex-col"
                >
                    {/* Header Modal */}
                    <div className="bg-sky-500 p-3 sm:p-4 xl:p-3 2xl:p-3.5 3xl:p-4 flex items-center gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 relative">
                        {/* Bouton fermer */}
                        <button
                            onClick={onClose}
                            className="absolute top-2 right-2 sm:top-3 sm:right-3 xl:top-2 xl:right-2 bg-white/20 hover:bg-white/30 border-none rounded-lg w-7 h-7 sm:w-8 sm:h-8 xl:w-7 xl:h-7 cursor-pointer flex items-center justify-center transition-colors"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                                <path d="M18 6L6 18M6 6l12 12" stroke="white" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>

                        {/* Avatar */}
                        <div
                            className="w-10 h-10 sm:w-12 sm:h-12 xl:w-10 xl:h-10 2xl:w-11 2xl:h-11 3xl:w-12 3xl:h-12 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg xl:text-base 2xl:text-lg border-2 border-white/30 flex-shrink-0"
                            style={{ backgroundColor: avatarColor }}
                        >
                            {initial}
                        </div>

                        {/* Infos élève */}
                        <div className="flex-1 min-w-0">
                            <h2 className="text-white m-0 text-base sm:text-lg xl:text-sm 2xl:text-base 3xl:text-lg font-semibold truncate">
                                {prenom} {nom}
                            </h2>
                            <p className="text-white/80 mt-0.5 text-xs xl:text-[11px] 2xl:text-xs 3xl:text-xs truncate">
                                Bilan Évaluation Nationale • {eleve.classe_nom}
                            </p>
                        </div>

                        {/* Score */}
                        <div className="hidden sm:block bg-white rounded-lg px-3 py-2 xl:px-2.5 xl:py-1.5 2xl:px-3 2xl:py-2 text-center mr-6 sm:mr-8 xl:mr-6">
                            <div
                                className="text-xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold"
                                style={{ color: getScoreColor(stats.score) }}
                            >
                                {stats.score}%
                            </div>
                            <div className="text-[10px] xl:text-[9px] 2xl:text-[10px] text-slate-500 mt-0.5">Score global</div>
                        </div>
                    </div>

                    {/* Stats rapides */}
                    <div className="flex gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 border-b border-slate-200 bg-slate-50">
                        <div className="flex-1 bg-emerald-100 rounded-lg xl:rounded-md p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 text-center">
                            <div className="text-xl sm:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-emerald-500">
                                {stats.reussites}
                            </div>
                            <div className="text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-emerald-700 mt-0.5">✅ Réussites</div>
                        </div>
                        <div className="flex-1 bg-red-100 rounded-lg xl:rounded-md p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 text-center">
                            <div className="text-xl sm:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-red-500">
                                {stats.echecs}
                            </div>
                            <div className="text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-red-700 mt-0.5">❌ Échecs</div>
                        </div>
                        <div className="flex-1 bg-sky-100 rounded-lg xl:rounded-md p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 text-center">
                            <div className="text-xl sm:text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-sky-500">
                                {stats.total}
                            </div>
                            <div className="text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-sky-700 mt-0.5">📝 Questions</div>
                        </div>
                        {/* Score mobile only */}
                        <div className="flex-1 sm:hidden bg-white rounded-lg p-2 text-center border border-slate-200">
                            <div
                                className="text-xl font-bold"
                                style={{ color: getScoreColor(stats.score) }}
                            >
                                {stats.score}%
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5">Score</div>
                        </div>
                    </div>

                    {/* Onglets catégories */}
                    {categories.length > 0 && (
                        <div className="px-3 sm:px-4 xl:px-3 2xl:px-3.5 3xl:px-4 py-2 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 border-b border-slate-200 bg-slate-50">
                            <div className="flex items-center gap-1.5 sm:gap-2 xl:gap-1.5 mb-2 xl:mb-1.5 2xl:mb-2">
                                <div className="w-4 h-4 xl:w-3.5 xl:h-3.5 2xl:w-4 2xl:h-4 rounded bg-gradient-to-br from-sky-500 to-cyan-500 flex items-center justify-center">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                                        <path d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </div>
                                <span className="text-xs xl:text-[11px] 2xl:text-xs font-semibold text-slate-600">
                                    Catégories
                                </span>
                            </div>
                            <div className="flex gap-2 xl:gap-1.5 2xl:gap-2 overflow-x-auto pb-1">
                                {categories.map(cat => {
                                    const isActive = activeTab === cat.key;
                                    return (
                                        <button
                                            key={cat.key}
                                            onClick={() => setActiveTab(cat.key)}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 xl:px-2.5 xl:py-1.5 2xl:px-3 2xl:py-1.5 3xl:px-4 3xl:py-2 rounded-lg xl:rounded-md border-none cursor-pointer text-xs xl:text-[11px] 2xl:text-xs 3xl:text-xs font-bold whitespace-nowrap transition-all duration-300 ${
                                                isActive
                                                    ? 'bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-500 text-white shadow-lg shadow-sky-500/40 scale-[1.02]'
                                                    : 'bg-white text-slate-500 shadow-sm hover:text-sky-500 hover:bg-sky-50 hover:shadow-md'
                                            }`}
                                        >
                                            {cat.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Liste des réponses - scrollable */}
                    <div className="flex-1 overflow-y-auto p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4">
                        <h3 className="m-0 mb-2 sm:mb-3 xl:mb-2 2xl:mb-2.5 3xl:mb-3 text-sm xl:text-xs 2xl:text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                            📋 Détail des réponses
                        </h3>

                        <div className="flex flex-col gap-2 xl:gap-1.5 2xl:gap-2 3xl:gap-2.5">
                            {eleveReponses.length === 0 ? (
                                <div className="text-center py-8 xl:py-6 text-slate-500 bg-slate-50 rounded-lg text-sm xl:text-xs">
                                    Aucune réponse dans cette catégorie
                                </div>
                            ) : (
                                eleveReponses.map((reponse) => (
                                    <div
                                        key={reponse.questionId}
                                        className={`flex items-center gap-2 sm:gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 p-2 sm:p-3 xl:p-2 2xl:p-2.5 3xl:p-3 rounded-lg xl:rounded-md transition-transform ${
                                            reponse.isCorrect
                                                ? 'bg-emerald-50 border border-emerald-200'
                                                : 'bg-red-50 border border-red-200'
                                        }`}
                                    >
                                        {/* Badge question */}
                                        <div className={`rounded-md px-2 py-1 xl:px-1.5 xl:py-0.5 2xl:px-2 2xl:py-1 text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs font-bold min-w-[36px] xl:min-w-[32px] 2xl:min-w-[36px] text-center text-white ${
                                            reponse.isCorrect ? 'bg-emerald-500' : 'bg-red-500'
                                        }`}>
                                            {reponse.questionId}
                                        </div>

                                        {/* Contenu */}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs sm:text-sm xl:text-[11px] 2xl:text-xs 3xl:text-sm font-medium text-slate-800 mb-0.5 leading-tight line-clamp-2">
                                                {reponse.questionLabel}
                                            </div>
                                            <div className="text-[10px] sm:text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-slate-500 flex flex-wrap items-center gap-1">
                                                <span>Rép:</span>
                                                <span className={`font-semibold px-1.5 py-0.5 rounded ${
                                                    reponse.isCorrect
                                                        ? 'text-emerald-600 bg-emerald-100'
                                                        : 'text-red-600 bg-red-100'
                                                }`}>
                                                    {reponse.reponse}
                                                </span>
                                                {!reponse.isCorrect && (
                                                    <>
                                                        <span className="text-slate-400">→</span>
                                                        <span className="font-semibold text-emerald-600 bg-emerald-100 px-1.5 py-0.5 rounded">
                                                            {reponse.bonneReponse}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Icône statut */}
                                        <div className={`w-6 h-6 sm:w-8 sm:h-8 xl:w-6 xl:h-6 2xl:w-7 2xl:h-7 3xl:w-8 3xl:h-8 rounded-full flex items-center justify-center text-white text-xs sm:text-sm xl:text-xs 2xl:text-sm font-bold flex-shrink-0 ${
                                            reponse.isCorrect ? 'bg-emerald-500' : 'bg-red-500'
                                        }`}>
                                            {reponse.isCorrect ? '✓' : '✗'}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Footer actions */}
                    <div className="p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 border-t border-slate-200 bg-slate-50">
                        <button
                            onClick={onClose}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white border-none rounded-lg py-2.5 sm:py-3 xl:py-2 2xl:py-2.5 3xl:py-3 text-xs sm:text-sm xl:text-xs 2xl:text-sm font-semibold cursor-pointer flex items-center justify-center gap-2 transition-colors"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            </div>

            {/* Styles d'animation */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.2s ease-out;
                }
                .animate-slideUp {
                    animation: slideUp 0.3s ease-out;
                }
            `}</style>
        </>
    );
}

export default ResultatEvalNatByEleve;
